import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Gauge, UsersThree, CurrencyCircleDollar, GearSix, UserCircle, Bell, SignOut, MonitorPlay, List,
  Package, CookingPot, CaretDown, CaretRight, Buildings, Timer,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { BrandWordmark } from "./Brand";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { fmtDateTime } from "../lib/format";
import { useLocation } from "react-router-dom";
import { AssistantBubble } from "./AssistantBubble";
import { Tour } from "./Tour";

const DASH = { to: "/dashboard", label: "Dashboard", tid: "nav-dashboard", icon: Gauge, roles: ["owner", "manager"], perm: "view_financials" };
const PLATFORM = { to: "/admin", label: "Platform", tid: "nav-admin", icon: Buildings, roles: ["owner", "manager", "staff"], platformAdmin: true };
const MY_PORTAL = { to: "/my", label: "My Portal", tid: "nav-my-portal", icon: UserCircle, roles: ["owner", "manager", "staff"] };
const PASSE = { to: "/passe", label: "MISE Passé", tid: "nav-passe", icon: Timer, roles: ["owner", "manager", "staff"], perm: "run_passe" };

const MGR = ["owner", "manager"];

const NAV_GROUPS = [
  { key: "finance", label: "Finance", icon: CurrencyCircleDollar, items: [
    { to: "/finance?tab=revenue", label: "Revenue", tid: "nav-fin-revenue", roles: MGR, permAny: ["view_financials", "capture_revenue"] },
    { to: "/finance?tab=expenses", label: "Expenses", tid: "nav-fin-expenses", roles: MGR, permAny: ["view_financials", "capture_invoices"] },
    { to: "/client-invoices", label: "Client Invoices", tid: "nav-client-invoices", roles: MGR, perm: "view_financials" },
    { to: "/functions", label: "Functions & Events", tid: "nav-functions", roles: MGR, perm: "view_financials" },
  ]},
  { key: "recipes", label: "Recipes", icon: CookingPot, items: [
    { to: "/recipes", label: "Recipe Book", tid: "nav-recipe-book", roles: MGR, perm: "manage_stock" },
    { to: "/recipes?tab=ingredients", label: "Ingredient Directory", tid: "nav-ingredients", roles: MGR, perm: "manage_stock" },
  ]},
  { key: "inventory", label: "Inventory", icon: Package, items: [
    { to: "/stocktake", label: "Stock Take", tid: "nav-stock-take", roles: MGR, perm: "manage_stock" },
    { to: "/stock?tab=holding", label: "Stock Holding", tid: "nav-stock-holding", roles: MGR, perm: "manage_stock" },
    { to: "/stock", label: "Stock Items & Review", tid: "nav-stock", roles: MGR, permAny: ["manage_stock", "capture_invoices"] },
    { to: "/suppliers", label: "Supplier Directory", tid: "nav-suppliers", roles: MGR, perm: "manage_stock" },
  ]},
  // POS group hidden while the venue runs on its existing POS system — code/routes intact
  // (restore the "pos" group here to re-enable).
  { key: "people", label: "People", icon: UsersThree, items: [
    { to: "/payroll", label: "Payroll", tid: "nav-payroll", roles: MGR, perm: "run_payroll" },
    { to: "/roster", label: "Roster", tid: "nav-roster", roles: MGR, perm: "manage_roster" },
    { to: "/leave", label: "Leave", tid: "nav-leave", roles: MGR, perm: "approve_leave" },
    { to: "/employees", label: "Staff Directory", tid: "nav-employees", roles: MGR, perm: "manage_employees" },
    { to: "/onboarding", label: "Onboarding", tid: "nav-onboarding", roles: MGR, perm: "manage_employees" },
    { to: "/training", label: "Training", tid: "nav-training", roles: MGR },
    { to: "/hours", label: "Hours", tid: "nav-hours-audit", roles: MGR, perm: "view_hours_audit" },
  ]},
  { key: "admin", label: "Settings & Admin", icon: GearSix, items: [
    { to: "/settings", label: "Settings", tid: "nav-settings", roles: ["owner"] },
    { to: "/permissions", label: "Permissions", tid: "nav-permissions", roles: ["owner"] },
    { to: "/audit", label: "Audit Log", tid: "nav-audit-log", roles: MGR, perm: "view_audit_log" },
  ]},
];

function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const onOpen = (open) => {
    if (open && data.unread > 0) {
      api.post("/notifications/read").then(load).catch(() => {});
    }
  };
  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2.5 rounded-md hover:bg-accent text-foreground transition-colors">
          <Bell size={22} />
          {data.unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 glass" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b font-heading font-semibold">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-4 text-sm text-muted-foreground">No notifications yet.</p>}
          {data.items.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${n.read ? "" : "bg-accent/50"}`} data-testid={`notification-${n.id}`}>
              <p className="text-sm font-medium">{n.title}</p>
              <p className="text-xs text-muted-foreground whitespace-pre-line mt-0.5">{n.body}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout, can, activeVenue, switchVenue } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("navCollapsed") || "{}"); } catch { return {}; }
  });
  const visible = (n) => {
    if (n.platformAdmin) return !!user.is_platform_admin;
    if (n.posFloor) return MGR.includes(user.role) || (user.role === "staff" && !!user.pos_operator);
    return n.roles.includes(user.role) &&
      (!n.perm || can(n.perm)) &&
      (!n.permAny || n.permAny.some((p) => can(p)));
  };

  const toggleGroup = (key) => {
    const next = { ...collapsed, [key]: !collapsed[key] };
    setCollapsed(next);
    localStorage.setItem("navCollapsed", JSON.stringify(next));
  };

  const navItem = (n) => {
    // active-state accounts for query-string targets (two items can share a pathname)
    const [nPath, nQuery] = n.to.split("?");
    const active = location.pathname === nPath && (nQuery ? location.search.includes(nQuery) : !NAV_GROUPS.flatMap((g) => g.items).some((o) => o.to.split("?")[0] === nPath && o.to.includes("?") && o.to !== n.to));
    return (
      <NavLink
        key={n.to + n.tid}
        to={n.to}
        end={n.to === "/"}
        onClick={() => setMobileOpen(false)}
        data-testid={n.tid}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
          active ? "bg-primary text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
        }`}
      >
        {n.icon && <n.icon size={19} weight="bold" />}
        {n.label}
      </NavLink>
    );
  };

  const sidebar = (
    <div className="flex flex-col h-full text-[#F7F7FA]" style={{ background: "linear-gradient(180deg, #0B0914 0%, #120E22 100%)" }}>
      <div className="px-6 pb-6 pt-safe-lg border-b border-white/10">
        <BrandWordmark size={28} />
        <p className="text-[11px] text-white/50 mt-1">Prepared Intelligence</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visible(DASH) && navItem(DASH)}
        {NAV_GROUPS.map((g) => {
          const gItems = g.items.filter(visible);
          if (gItems.length === 0) return null;
          const isCollapsed = collapsed[g.key] && !gItems.some((n) => location.pathname === n.to.split("?")[0]);
          return (
            <div key={g.key}>
              <button
                onClick={() => toggleGroup(g.key)}
                data-testid={`nav-group-${g.key}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider text-white/40 hover:text-white/70 transition-colors"
              >
                <g.icon size={15} weight="bold" />
                <span className="flex-1 text-left">{g.label}</span>
                {isCollapsed ? <CaretRight size={13} /> : <CaretDown size={13} />}
              </button>
              {!isCollapsed && <div className="space-y-0.5 mb-2">{gItems.map(navItem)}</div>}
            </div>
          );
        })}
        {visible(MY_PORTAL) && navItem(MY_PORTAL)}
        {visible(PASSE) && navItem(PASSE)}
        {visible(PLATFORM) && navItem(PLATFORM)}
      </nav>
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <a href="/kiosk" target="_blank" rel="noreferrer" data-testid="nav-kiosk-mode"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors">
          <MonitorPlay size={19} weight="bold" /> MISE Shift
        </a>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <SignOut size={19} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <aside className="hidden md:block fixed inset-y-0 left-0 w-64 z-30">{sidebar}</aside>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 z-50">{sidebar}</aside>
        </div>
      )}
      <div className="md:ml-64 relative z-10">
        <header className="sticky top-0 z-20 backdrop-blur-md border-b border-white/5 px-4 md:px-8 pb-3 pt-safe flex items-center justify-between" style={{ background: "rgba(7,5,10,0.85)" }}>
          <button className="md:hidden p-2.5 -ml-2" data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
            <List size={24} />
          </button>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            {user.role === "owner" && (user.venues || []).length > 0 && (
              <div className="flex items-center gap-1.5 pr-1 border-r border-border mr-1">
                <Buildings size={17} className="text-muted-foreground" />
                <select
                  data-testid="venue-switcher"
                  value={activeVenue}
                  disabled={(user.venues || []).length < 2}
                  onChange={(e) => switchVenue(e.target.value)}
                  className="text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer max-w-[140px] truncate"
                >
                  {user.venues.map((v) => (
                    <option key={v.id} value={v.id} data-testid={`venue-option-${v.id}`}>{v.name}</option>
                  ))}
                </select>
              </div>
            )}
            <NotificationsBell />
            <div className="text-right max-w-[120px] sm:max-w-none">
              <p className="text-sm font-medium leading-tight truncate" data-testid="header-user-name">{user.first_name} {user.last_name}</p>
              <p className="text-[11px] text-muted-foreground capitalize">{user.role}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-[1400px] pb-safe">
          <Outlet />
        </main>
      </div>
      <AssistantBubble />
      <Tour />
    </div>
  );
}
